import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Last-resort error boundary that wraps the entire App tree. When a render
 * throws (including the "Maximum call stack size exceeded" bug we hit on
 * iPhone), React invokes {@link componentDidCatch}; we paint a readable
 * crash page right on the screen with the message + component stack instead
 * of a silent white screen.
 *
 * Intentionally MUI-free so the boundary keeps working even if the theme
 * provider itself is the one that crashes.
 */
export class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Persist the React-side stack too — it tells us which component tree
    // was being rendered when the error fired.
    this.setState({ error, errorInfo });
    // Also surface in the console for desktop debugging.
    console.error('[RootErrorBoundary] Caught:', error, errorInfo);
  }

  render() {
    const { error, errorInfo } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          padding: '16px',
          background: '#1A1108',
          color: '#FFB880',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '13px',
          lineHeight: 1.45,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowY: 'auto',
        }}
      >
        <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: 12 }}>
          AfroEspace — crash au boot
        </div>
        <div style={{ marginBottom: 14 }}>
          Screenshot ce message et envoie-le.
        </div>

        <div style={{ color: '#FFD9B8', marginBottom: 4 }}>
          {error.name}: {error.message}
        </div>

        {error.stack && (
          <details open style={{ marginBottom: 14 }}>
            <summary style={{ cursor: 'pointer', color: '#FFD9B8' }}>
              JS stack
            </summary>
            <div style={{ marginTop: 6 }}>{error.stack}</div>
          </details>
        )}

        {errorInfo?.componentStack && (
          <details open>
            <summary style={{ cursor: 'pointer', color: '#FFD9B8' }}>
              React component stack
            </summary>
            <div style={{ marginTop: 6 }}>{errorInfo.componentStack}</div>
          </details>
        )}

        <div style={{ marginTop: 18, opacity: 0.7, fontSize: 11 }}>
          UA: {typeof navigator !== 'undefined' ? navigator.userAgent : '?'}
          {'\n'}screen:{' '}
          {typeof screen !== 'undefined'
            ? `${screen.width}x${screen.height}`
            : '?'}
        </div>
      </div>
    );
  }
}
